import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { Portal } from '../../common/Portal';
import { Toast } from '../Toast';

export interface ShareOption {
  id: string;
  label: string;
  icon: string;
  iconColor?: string;
  onClick: () => void;
}

interface SharePopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  url: string;
  shareOptions: ShareOption[];
}

export const SharePopup: React.FC<SharePopupProps> = ({
  isOpen,
  onClose,
  title = 'Share',
  url,
  shareOptions,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [showToast, setShowToast] = useState(false);

  const handleToastHide = useCallback(() => {
    setShowToast(false);
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      onClose();
      // Show toast after popup is closed
      setTimeout(() => setShowToast(true), 100);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleQuickShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title,
          url,
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          // TODO: Show error toast or tooltip
        }
      }
    }
  };

  const isShareSupported = typeof navigator !== 'undefined' && 'share' in navigator;

  return (
    <>
      {isOpen && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Popup */}
            <div 
              className={`
                relative w-full max-w-sm
                bg-surface-primary border border-border-primary
                rounded-2xl shadow-xl
                ${isRTL ? 'rtl' : 'ltr'}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border-primary">
                <h2 className="text-lg font-semibold text-text-primary">
                  {t('common.share')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 -m-2 hover:bg-surface-secondary rounded-lg transition-colors"
                  aria-label={t('common.close')}
                >
                  <Icon icon="ph:x-thin" className="w-5 h-5 text-text-primary" />
                </button>
              </div>

              {/* Share Options Grid */}
              <div className="grid grid-cols-4 gap-4 p-6">
                {shareOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={option.onClick}
                    className="flex flex-col items-center gap-2 group"
                  >
                    <div 
                      className={`
                        w-12 h-12 rounded-full
                        flex items-center justify-center
                        transition-transform duration-200
                        group-hover:scale-110
                        ${option.iconColor || 'bg-surface-secondary text-text-primary'}
                      `}
                    >
                      <Icon icon={option.icon} className="w-6 h-6" />
                    </div>
                    <span className="text-sm text-text-secondary text-center">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="p-4 border-t border-border-primary space-y-4">
                {isShareSupported && (
                  <button
                    onClick={handleQuickShare}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-secondary transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-surface-secondary flex items-center justify-center shrink-0">
                      <Icon icon="ph:share-network-thin" className="w-5 h-5 text-text-primary" />
                    </div>
                    <span className="text-sm font-medium text-text-primary">
                      {t('common.quickShare')}
                    </span>
                  </button>
                )}

                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-secondary transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-surface-secondary flex items-center justify-center shrink-0">
                    <Icon icon="ph:link-thin" className="w-5 h-5 text-text-primary" />
                  </div>
                  <span className="text-sm font-medium text-text-primary">
                    {t('common.copyLink')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
      <Toast 
        message={t('common.linkCopied')}
        isVisible={showToast}
        onHide={handleToastHide}
      />
    </>
  );
};

export default SharePopup; 